import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
} from '@ps20-frontend/frontend-models';
import {
  Berufsrisikogruppe as BffBerufsrisikogruppe,
  Feature,
  Produktlinienname,
  SpartenType,
  UriDruckdaten,
  VersichertePersonDruck,
  ZusatzoptionenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  getSelectedProduktlinie,
  mapGesamtbeitragIfJaehrlichAndBeitragIsWrong,
} from '@ps20-frontend/preis-lib';
import { selectFeatureToggle, selectRegexp } from '@ps20-frontend/selector';
import { convertDate } from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import { selectSelectedProductZusatzoptionItems } from '../selector/produkt.selector';
import { Vp } from '../model/vp';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.UNF));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.UNF)) {
      yield cancel();
      return;
    }

    const uriDruckdaten: UriDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(uriDruckdaten));
  } catch (e) {
    PS20Logger.error(App.UNF, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): UriDruckdaten {
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectSelectedProductZusatzoptionItems(state),
  );
  const ausgewaehlteProduktlinie = getSelectedProduktlinie(state.produkt);
  const produktLinieM = state.produkt.produktlinien.find(
    prodline => prodline.produktName === Produktlinienname.SMART,
  );
  const produktLinieL = state.produkt.produktlinien.find(
    prodline => prodline.produktName === Produktlinienname.KOMFORT,
  );
  const produktLinieXl = state.produkt.produktlinien.find(
    prodline => prodline.produktName === Produktlinienname.PREMIUM,
  );
  const invaliditaetsleistungMax = 'invaliditaetsleistungMax';
  const invaliditaetssumme = 'invaliditaetssumme';
  const todesfall = 'todesfall';
  const topschutz = 'topschutz';
  const leistungAb = 'invaliditaetsgrad';
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    treuebonus: state.taasInfo.tbNachlass,
    zaehlstuecke: state.rabattstufe,
    spartenType: SpartenType.UNFALL,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    uriTabelleDruck: state.vertragsoptionen.beibehalten
      ? undefined
      : {
          invaliditaetLeistungL: Number.parseInt(
            '' +
              produktLinieL.leistungen.find(lst => lst.id === invaliditaetsleistungMax).eigenschaft,
            10,
          ),
          invaliditaetLeistungXl: Number.parseInt(
            '' +
              produktLinieXl.leistungen.find(lst => lst.id === invaliditaetsleistungMax)
                .eigenschaft,
            10,
          ),
          invaliditaetsSummeL: Number.parseInt(
            '' + produktLinieL.leistungen.find(lst => lst.id === invaliditaetssumme).eigenschaft,
            10,
          ),
          invaliditaetsSummeXl: Number.parseInt(
            '' + produktLinieXl.leistungen.find(lst => lst.id === invaliditaetssumme).eigenschaft,
            10,
          ),
          todesfallLeistungL: Number.parseInt(
            '' + produktLinieL.leistungen.find(lst => lst.id === todesfall).eigenschaft,
            10,
          ),
          todesfallLeistungXl: Number.parseInt(
            '' + produktLinieXl.leistungen.find(lst => lst.id === todesfall).eigenschaft,
            10,
          ),
          leistungAbL: Number.parseInt(
            '' + produktLinieL.leistungen.find(lst => lst.id === leistungAb).eigenschaft,
            10,
          ),
          isTopSchutzM:
            produktLinieM.leistungen.find(lst => lst.id === topschutz).eigenschaft === 'Inklusive',
          isTopSchutzSelected:
            ausgewaehlteProduktlinie.leistungen.find(lst => lst.id === topschutz).eigenschaft ===
            'Inklusive',
          isInvGrad20Selected:
            ausgewaehlteProduktlinie.leistungen.find(lst => lst.id === leistungAb).eigenschaft ===
            '20',
        },
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.UNF,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(
      ausgewaehlteProduktlinie,
      SpartenType.UNFALL,
      null,
      selectRegexp(state, 'TEXT'),
    ),
    versichertePerson: convertToBffVersichertePerson(state.vp),
    berufsrisikogruppe: (BffBerufsrisikogruppe as Record<string, BffBerufsrisikogruppe>)[state.vp.berufsrisikogruppe],
    zusatzoptionenDruck,
    vorversicherungenDruck: null,
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    hasDynamik: false,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

function convertToBffVersichertePerson(vp: Vp): VersichertePersonDruck {
  return {
    geburtsdatum: convertDate(vp.geburtsdatum),
    anrede: vp.anrede,
    firmenname: '',
    businessForm: null,
    juristisch: vp.juristisch,
    personenID: vp.kundenverwaltungID,
    nachname: vp.nachname,
    vorname: vp.vorname,
    akadgrad: vp.akadgrad?.text,
    adelsbez: vp.adelsbez?.text,
    adelstitel: vp.adelstitel?.text,
    adresse: {
      plz: vp.adresse.plz,
      ort: vp.adresse.ort,
      strasse: vp.adresse.strasse,
      hausnummer: vp.adresse.hausnummer,
      ortsteil: vp.adresse.ortsteil,
      hausnrzus: vp.adresse.hausnrzus,
      land: {
        wert: vp.adresse.land?.wert || 'DE',
        text: vp.adresse.land?.text || 'Deutschland',
      },
    },
  } as VersichertePersonDruck;
}
